import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../pages/api/axios";
import { aggregateMastersheetRent } from "../utils/chart/mastersheetRent";

const cache = new Map();
const inflight = new Map();
const TTL_MS = 60_000;

const fetchMastersheet = (propertyId) => {
  const key = `master_sheet:${propertyId}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.t < TTL_MS) {
    return Promise.resolve(cached.items);
  }
  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = axios
    .get("v2/master_sheet", {
      params: {
        property_id: propertyId,
        building_name: "",
        filters: {},
      },
    })
    .then((res) => {
      const payload = res && res.data ? res.data : {};
      const items = Array.isArray(payload.items)
        ? payload.items
        : Array.isArray(payload.data?.items)
        ? payload.data.items
        : Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      cache.set(key, { items, t: Date.now() });
      inflight.delete(key);
      return items;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
};

/**
 * Fetches Mastersheet items for the selected property and aggregates them
 * into a monthly "Rent Billed" series.
 *
 * @param {string} propertyId  property to query; if falsy, hook is idle
 * @param {Object} range       { start, end } as "YYYY-MM-DD"
 * @returns {{
 *   data: { categories: string[], monthly: number[], total: number },
 *   loading: boolean,
 *   error: string | null,
 *   needsProperty: boolean
 * }}
 */
export const useRentBilled = (propertyId, range) => {
  const [state, setState] = useState({
    data: { categories: [], monthly: [], total: 0 },
    loading: false,
    error: null,
  });

  const abortRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!propertyId) {
      setState({
        data: { categories: [], monthly: [], total: 0 },
        loading: false,
        error: null,
      });
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));
    abortRef.current = false;

    try {
      const items = await fetchMastersheet(propertyId);
      if (abortRef.current) return;
      const aggregated = aggregateMastersheetRent(items, range);
      setState({ data: aggregated, loading: false, error: null });
    } catch (err) {
      if (abortRef.current) return;
      setState({
        data: { categories: [], monthly: [], total: 0 },
        loading: false,
        error: err && err.message ? err.message : "Failed to load rent data",
      });
    }
  }, [propertyId, range && range.start, range && range.end]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current = true;
    };
  }, [fetchData]);

  return {
    ...state,
    needsProperty: !propertyId,
  };
};

export default useRentBilled;
